import { useEffect, useState } from 'react';

import { DownloadIcon } from 'lucide-react';
import mime from 'mime';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { createMediaUrl } from '@/modules/humanChat/hooks/useAttachment';
import { popModal } from '@/redux/modals/actions';
import { downloadFromUrl } from '@/util/dom';

interface Props {
  url?: string;
  file?: Buffer;
  title: string;
  mime_type: string;
}

export const ModalMediaPreview = ({ url, title, mime_type, file }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [mediaUrl, setMediaUrl] = useState<string>(url);
  // Tracked by source rather than as a boolean, so swapping in the blob url (or a new preview,
  // which the modal conductor renders without a key) gets a fresh attempt instead of a stuck error.
  const [erroredUrl, setErroredUrl] = useState<string | null>(null);
  const videoError = erroredUrl === mediaUrl;
  // Callers may open a preview with no title (ImageStack passes an empty string). A download with
  // no name is saved as "undefined" with no extension, so name it from the mime type instead.
  const extension = mime.getExtension(mime_type);
  const downloadName = title || (extension ? `media.${extension}` : '');

  useEffect(() => {
    if (file) {
      const { url: createdUrl, cleanup } = createMediaUrl(file as Buffer, mime_type);
      if (createdUrl) {
        setMediaUrl(createdUrl);
      }
      return cleanup;
    }
  }, [mime_type, file]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      dispatch(popModal());
    }
  };

  const getMedia = () => {
    if (mime_type?.includes('image')) {
      return (
        <img
          src={mediaUrl}
          alt={title}
          className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
        />
      );
    }
    if (mime_type?.includes('video')) {
      return (
        <div className="flex flex-col items-center gap-3">
          {videoError ? (
            <p className="rounded-lg bg-background px-6 py-8 text-sm leading-5 text-foreground">
              {t('modals.video_playback_failed')}
            </p>
          ) : (
            <video
              src={mediaUrl}
              controls
              autoPlay
              className="max-h-[80vh] max-w-[90vw] rounded-lg shadow-2xl"
              onError={() => setErroredUrl(mediaUrl)}
            />
          )}
          {/* Permanent, not error-gated: a codec the browser cannot decode still loads fine and
              never fires onError, so gating this on the error would miss the HEVC case it is for.
              Measured with an iPhone .mov: loadeddata fires, duration is right, dimensions are 0. */}
          {mediaUrl ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => downloadFromUrl(mediaUrl, downloadName)}
            >
              <DownloadIcon size={16} />
              {t('common.download')}
            </Button>
          ) : null}
        </div>
      );
    }
    if (mime_type?.includes('audio')) {
      return <audio src={mediaUrl} controls autoPlay className="w-80" />;
    }
    return null;
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent
        tabIndex={-1}
        // Direct focus to the dialog container instead of the close button, so opening
        // doesn't surface the button's focus ring + Esc tooltip while keeping focus inside the dialog.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          if (event.currentTarget instanceof HTMLElement) {
            event.currentTarget.focus();
          }
        }}
        className="flex w-auto max-w-[90vw] items-center justify-center border-0 bg-transparent p-0 shadow-none outline-none sm:max-w-[90vw]"
      >
        <DialogTitle className="sr-only">{title || t('modals.media_preview')}</DialogTitle>
        {getMedia()}
      </DialogContent>
    </Dialog>
  );
};
